import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetUser from '@/hooks/useGetUser';
import { composeUri } from '@/utils/compose';
import {
    BIG_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_CLIENT_SITE_MANAGER,
    BIG_CLIENT_SITE_OPERATOR,
    BIG_MANAGER_TAG,
    BIG_OPERATOR,
    BIG_SUB_MANAGER_CLIENT_TAG,
    BIG_SUB_MANAGER_SITE_TAG,
    BIG_SUPER_ADMIN,
    MANAGER_TAG,
    PROFILES,
    SUB_MANAGER_TAG,
    USER_PROFILES,
    USERS,
} from '@/utils/CONST';
import { EditOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { compact, nth } from 'lodash';
import {
    Form,
    required,
    SelectInput,
    TextInput,
    useDataProvider,
    useNotify,
    useTranslate,
    useUpdate,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router';

/**
 * Construye el array de tags del usuario basado en el tag seleccionado.
 * Asigna tags jerárquicos según el perfil seleccionado.
 */
const buildUserTags = (selectedTag: string): string[] => {
    const tags: (string | undefined)[] = [];

    // Determinar tags de nivel de manager
    const isHighLevelManager = [BIG_SUPER_ADMIN, BIG_ADMIN, BIG_OPERATOR].includes(selectedTag);
    if (isHighLevelManager) {
        tags.push(MANAGER_TAG, BIG_MANAGER_TAG);
    } else {
        tags.push(SUB_MANAGER_TAG);
    }

    // Agregar tag de sub-manager de cliente si aplica
    if (selectedTag === BIG_AGENCY_MANAGER) {
        tags.push(BIG_SUB_MANAGER_CLIENT_TAG);
    }

    // Agregar tag de sub-manager de sitio si aplica
    const isSiteManager = [BIG_CLIENT_SITE_MANAGER, BIG_CLIENT_SITE_OPERATOR, BIG_AGENCY_OPERATOR].includes(
        selectedTag,
    );
    if (isSiteManager) {
        tags.push(BIG_SUB_MANAGER_SITE_TAG);
    }

    // Agregar el tag seleccionado original
    tags.push(selectedTag);

    // Eliminar valores undefined/null
    return compact(tags);
};

const EditAgencyUser = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const notify = useNotify();
    const { userId } = useParams();
    const { user, isLoading: isLoadingUser } = useGetUser({ userId: userId ?? '' });
    const dataProvider = useDataProvider();

    const navigateBack = () => {
        navigate(-1);
    };

    const handleSubmit = async (values: any) => {
        // Obtener el perfil usando el dataProvider
        let profileId = null;
        if (values.tags) {
            try {
                const profileResponse = await dataProvider.getList(PROFILES, {
                    pagination: {
                        page: 1,
                        perPage: 1,
                    },
                    filter: {
                        internalName: values.tags,
                    },
                });
                const profile = nth(profileResponse.data);
                profileId = profile?.id;
            } catch (error) {
                notify('ra.notification.http_error', { type: 'error' });
                return;
            }
        }

        const user_tags = buildUserTags(values.tags);
        delete values.tags;

        update(
            USERS,
            {
                id: composeUri(USERS, userId),
                data: {
                    ...values,
                    tags: user_tags,
                    profiles: profileId ? [profileId] : [],
                },
            },
            {
                onSuccess: () => {
                    notify('ra.notification.updated', { type: 'success' });
                    navigateBack();
                },
            },
        );
    };

    return (
        <DialogTemplate
            closeButtonDisabled={isLoadingUpdate}
            title={translate('main.navigation.edit_user')}
            icon={<EditOutlined />}
            open={true}
        >
            <Form
                defaultValues={{
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    tags: user?.tags.filter((v: string) => USER_PROFILES.includes(v)),
                }}
                onSubmit={handleSubmit}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            helperText={false}
                            source='lastName'
                            label='main.navigation.last_name'
                            validate={required()}
                        />

                        <TextInput
                            helperText={false}
                            source='firstName'
                            label='main.navigation.name'
                            validate={required()}
                        />

                        <SelectInput
                            helperText={false}
                            label={'users.list.user_type'}
                            source={'tags'}
                            choices={USER_PROFILES.map((v: string) => ({
                                id: v,
                                name: `users.profiles.${v.toLowerCase()}`,
                            }))}
                            validate={required()}
                        />
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={navigateBack} disabled={isLoadingUpdate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton loading={isLoadingUpdate || isLoadingUser} type={'submit'} variant={'contained'}>
                            {translate('ra.action.edit')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default EditAgencyUser;
